import React from 'react';
import { AlertTriangle, DollarSign, FileText } from 'lucide-react';
import { cn } from '@utils/cn';
import { useTranslation } from '@hooks/useTranslation';
import {
  TriageFilter,
  TriageFilterBarProps,
} from './TriageFilterBar.types';

interface PillConfig {
  key: Exclude<TriageFilter, 'all'>;
  icon: React.ReactNode;
  activeClasses: string;
  countClasses: string;
}

// `label` deliberately not part of this table — it's read from the catalog inside the
// component below (`dashboard.triage*Label`), since a module-level constant can't call
// `useTranslation`.
const PILLS: PillConfig[] = [
  {
    key: 'incident',
    icon: <AlertTriangle className="w-3.5 h-3.5" />,
    activeClasses: 'bg-red-50 border-red-200 text-red-700',
    countClasses: 'bg-red-100 text-red-700',
  },
  {
    key: 'budget',
    icon: <DollarSign className="w-3.5 h-3.5" />,
    activeClasses: 'bg-orange-50 border-orange-200 text-orange-700',
    countClasses: 'bg-orange-100 text-orange-700',
  },
  {
    key: 'invoice',
    icon: <FileText className="w-3.5 h-3.5" />,
    activeClasses: 'bg-blue-50 border-blue-200 text-blue-700',
    countClasses: 'bg-blue-100 text-blue-700',
  },
];

// Dashboard triage filter strip. The "All" pill is always shown; each typed pill
// appears only when its count > 0 (to draw attention) with its count badge.
export const TriageFilterBar: React.FC<TriageFilterBarProps> = ({
  activeFilter,
  onChange,
  counts,
  canInvoice = true,
  trailingSlot,
  className = '',
}) => {
  const t = useTranslation('dashboard');
  const pillLabels: Record<Exclude<TriageFilter, 'all'>, string> = {
    incident: t.triageIncidentsLabel,
    budget: t.triageBidsLabel,
    invoice: t.triageInvoicesLabel,
  };

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onChange('all')}
        className={cn(
          'px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap',
          activeFilter === 'all'
            ? 'bg-gray-800 text-white'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
        )}
      >
        {t.triageAllLabel}
      </button>

      {PILLS.map((pill) => {
        const count = counts[pill.key];
        if (count <= 0) return null;
        if (pill.key === 'invoice' && !canInvoice) return null;
        const isActive = activeFilter === pill.key;

        return (
          <button
            key={pill.key}
            type="button"
            onClick={() => onChange(pill.key)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap border',
              isActive
                ? pill.activeClasses
                : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50',
            )}
          >
            {pill.icon}
            {pillLabels[pill.key]}
            <span
              className={cn(
                'px-1.5 py-0.5 rounded-full text-[11px]',
                pill.countClasses,
              )}
            >
              {count}
            </span>
          </button>
        );
      })}

      {/* Non-filter pill(s) appended to the row, e.g. the warehouse alerts pill
          that opens its own slide-over instead of filtering the feed. */}
      {trailingSlot}
    </div>
  );
};

export default TriageFilterBar;
